/* eslint-disable no-underscore-dangle */
/* eslint-disable max-len */
import { NumberConversionService } from '../../../shared/converter/number-converter.service';
import {
  OfferData,
  OfferFinancingData,
  OfferLeasingData,
  OfferResidualLeasingData,
  OfferStub,
} from '../../../src/app/@shared-types/opportunites/offer';
import { AutoboardVehicle } from '../../../src/app/@shared-types/vehicle';
import { OfferFormData } from '../../../src/app/@types/offer';

/* @ngInject */
export default class OfferFormService {
  private numberConversionService: NumberConversionService;

  constructor(
    numberConversionService: NumberConversionService,
  ) {
    this.numberConversionService = numberConversionService;
  }

  public getGeneralOfferStub = (
    offerData: OfferFormData,
    offerFile: string | null,
    vehicle: AutoboardVehicle,
    dummyVehicle: boolean,
  ): OfferStub<OfferData> => {
    if (!offerData.customerId) throw new Error('No customer selected');
    if (!offerData.generalData.extras) throw new Error('No extras defined');

    /** Create basic offer stub that is universal for all offer types. */
    const generalStub: OfferStub<OfferData> = {
      customerId: offerData.customerId,
      data: {
        includesVat: offerData.generalData.includesVat || false,
        validDate: this.numberConversionService.frontendStringToDate(String(offerData.generalData.validDate)),
        extras: offerData.generalData.extras.map((e: any) => ({ description: e.description, price: this.numberConversionService.parseStringFloat(e.price) })),
        additionalInfo: '',
      },
      vehicleId: String(vehicle._id),
      type: offerData.type,
      offerFile,
      dummyVehicle,
    };

    if (offerData.generalData.rrp) generalStub.data.rrp = this.numberConversionService.parseStringFloat(offerData.generalData.rrp);
    if (offerData.generalData.offerPrice) generalStub.data.offerPrice = this.numberConversionService.parseStringFloat(offerData.generalData.offerPrice);
    if (offerData.generalData.discount) generalStub.data.discount = this.numberConversionService.parseStringFloat(offerData.generalData.discount);
    if (offerData.generalData.additionalInfo) generalStub.data.additionalInfo = String(offerData.generalData.additionalInfo);

    return generalStub;
  };

  public getLeasingOfferStub = (
    generalStub: OfferStub<OfferData>,
    offerData: OfferFormData,
  ): OfferStub<OfferLeasingData> => {
    /** Add data for offer type Leasing */
    if (offerData.leasingData.chargeMileage === undefined
      || offerData.leasingData.countPayments === undefined
      || offerData.leasingData.monthlyRate === undefined
      || offerData.leasingData.firstPayment === undefined
      || offerData.leasingData.countPayments === undefined
      || offerData.leasingData.totalMileage === undefined
      || offerData.leasingData.refundMileage === undefined
      || offerData.leasingData.bankInformation === undefined
    ) throw new Error('Not all Leasing data defined');

    const leasingStub: OfferStub<OfferLeasingData> = {
      ...generalStub,
      data: {
        ...generalStub.data,
        chargeMileage: this.numberConversionService.parseStringFloat(offerData.leasingData.chargeMileage),
        countPayments: this.numberConversionService.parseStringFloat(offerData.leasingData.countPayments),
        firstPayment: this.numberConversionService.parseStringFloat(offerData.leasingData.firstPayment),
        optionToBuy: offerData.leasingData.optionToBuy || false,
        monthlyRate: this.numberConversionService.parseStringFloat(offerData.leasingData.monthlyRate),
        refundMileage: this.numberConversionService.parseStringFloat(offerData.leasingData.refundMileage),
        totalMileage: this.numberConversionService.parseStringFloat(offerData.leasingData.totalMileage),
        bankInformation: offerData.leasingData.bankInformation,
      },
    };

    return leasingStub;
  };

  public getResidualLeasingStub = (
    generalStub: OfferStub<OfferData>,
    offerData: OfferFormData,
  ): OfferStub<OfferResidualLeasingData> => {
    if (
      !offerData.customerId
      || offerData.residualData.annualRate === undefined
      || offerData.residualData.chargeRate === undefined
      || offerData.residualData.countPayments === undefined
      || offerData.residualData.firstPayment === undefined
      || offerData.residualData.includesVat === undefined
      || offerData.residualData.monthlyRate === undefined
      || offerData.residualData.optionToBuy === undefined
      || offerData.residualData.residualValue === undefined
      || offerData.residualData.sumTotal === undefined
      || offerData.residualData.bankInformation === undefined
    ) throw new Error('Not all Residual Leasing data defined');

    const leasingStub: OfferStub<OfferResidualLeasingData> = {
      ...generalStub,
      data: {
        ...generalStub.data,
        countPayments: this.numberConversionService.parseStringFloat(offerData.residualData.countPayments),
        annualRate: this.numberConversionService.parseStringFloat(offerData.residualData.annualRate),
        chargeRate: this.numberConversionService.parseStringFloat(offerData.residualData.chargeRate),
        firstPayment: this.numberConversionService.parseStringFloat(offerData.residualData.firstPayment),
        monthlyRate: this.numberConversionService.parseStringFloat(offerData.residualData.monthlyRate),
        optionToBuy: offerData.residualData.optionToBuy || false,
        residualValue: this.numberConversionService.parseStringFloat(offerData.residualData.residualValue),
        sumTotal: this.numberConversionService.parseStringFloat(offerData.residualData.sumTotal),
        bankInformation: offerData.residualData.bankInformation,
      },
    };

    return leasingStub;
  };

  public getFinancingStub = (
    generalStub: OfferStub<OfferData>,
    offerData: OfferFormData,
  ): OfferStub<OfferFinancingData> => {
    if (
      !offerData.customerId
      || offerData.financingData.firstPayment === undefined
      || offerData.financingData.loanTotal === undefined
      || offerData.financingData.sumTotal === undefined
      || offerData.financingData.annualRate === undefined
      || offerData.financingData.chargeRate === undefined
      || offerData.financingData.countPayments === undefined
      || offerData.financingData.firstRate === undefined
      || offerData.financingData.monthlyRate === undefined
      || offerData.financingData.bankInformation === undefined
    ) throw new Error('Not all Financing data defined');

    const financingStub: OfferStub<OfferFinancingData> = {
      ...generalStub,
      data: {
        ...generalStub.data,
        firstPayment: this.numberConversionService.parseStringFloat(offerData.financingData.firstPayment),
        loanTotal: this.numberConversionService.parseStringFloat(offerData.financingData.loanTotal),
        sumTotal: this.numberConversionService.parseStringFloat(offerData.financingData.sumTotal),
        annualRate: this.numberConversionService.parseStringFloat(offerData.financingData.annualRate),
        chargeRate: this.numberConversionService.parseStringFloat(offerData.financingData.chargeRate),
        firstRate: this.numberConversionService.parseStringFloat(offerData.financingData.firstRate),
        monthlyRate: this.numberConversionService.parseStringFloat(offerData.financingData.monthlyRate),
        countPayments: parseInt(String(offerData.financingData.countPayments), 10),
        bankInformation: offerData.financingData.bankInformation,
      },
    };

    if (offerData.financingData.lastRate) financingStub.data.lastRate = this.numberConversionService.parseStringFloat(offerData.financingData.lastRate);

    return financingStub;
  };
}
